import { useState } from 'react';
import {
    Create,
    SimpleForm,
    TextInput,
    NumberInput,
    useNotify,
    useRedirect,
    useDataProvider,
} from 'react-admin';
import { Card, CardContent, Typography, Alert, TextField as MuiTextField, Button, Box } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopyOutlined';

interface TokenCreateData {
    name: string;
    expirationDays: number;
    maxUsage?: number;
    rateLimitPerHour: number;
}

interface CreatedToken {
    token: string;
    name: string;
    expiresAt: string;
    maxUsage: number | null;
    rateLimitPerHour: number;
}

export const BootstrapTokenCreate = () => {
    const [generatedToken, setGeneratedToken] = useState<string | null>(null);
    const [tokenData, setTokenData] = useState<CreatedToken | null>(null);
    const notify = useNotify();
    const redirect = useRedirect();
    const dataProvider = useDataProvider();

    const handleSubmit = async (data: Partial<TokenCreateData>) => {
        try {
            const result = await dataProvider.create('bootstrap-tokens', { data });
            setGeneratedToken(result.data.token);
            setTokenData(result.data as CreatedToken);
            notify('Token created successfully', { type: 'success' });
        } catch (error: unknown) {
            const message = error instanceof Error ? error.message : 'Failed to create token';
            notify(message, { type: 'error' });
        }
    };

    const copyToken = () => {
        if (generatedToken) {
            navigator.clipboard.writeText(generatedToken);
            notify('Token copied to clipboard', { type: 'success' });
        }
    };

    if (generatedToken && tokenData) {
        return (
            <Card sx={{ maxWidth: 600, margin: 'auto', mt: 4 }}>
                <CardContent>
                    <Typography variant="h5" color="success.main" gutterBottom>
                        Token Created Successfully
                    </Typography>

                    <Alert severity="warning" sx={{ mb: 3 }}>
                        <strong>Important:</strong> This token will only be shown once.
                        Copy it now and store it securely.
                    </Alert>

                    <Box sx={{ mb: 3 }}>
                        <Typography variant="subtitle2" gutterBottom>
                            Bootstrap Token
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <MuiTextField
                                fullWidth
                                value={generatedToken}
                                InputProps={{
                                    readOnly: true,
                                    sx: { fontFamily: 'monospace' }
                                }}
                            />
                            <Button
                                variant="outlined"
                                onClick={copyToken}
                                startIcon={<ContentCopyIcon />}
                            >
                                Copy
                            </Button>
                        </Box>
                    </Box>

                    <Typography variant="body2" color="text.secondary">
                        <strong>Name:</strong> {tokenData.name}<br />
                        <strong>Expires:</strong> {new Date(tokenData.expiresAt).toLocaleString()}<br />
                        <strong>Max Usage:</strong> {tokenData.maxUsage || 'Unlimited'}<br />
                        <strong>Rate Limit:</strong> {tokenData.rateLimitPerHour} / hour
                    </Typography>

                    <Box sx={{ mt: 3 }}>
                        <Button
                            variant="contained"
                            onClick={() => redirect('/bootstrap-tokens')}
                        >
                            Back to Token List
                        </Button>
                    </Box>
                </CardContent>
            </Card>
        );
    }

    return (
        <Create>
            <SimpleForm onSubmit={handleSubmit}>
                <TextInput
                    source="name"
                    required
                    helperText="Use lowercase letters, numbers, and hyphens only"
                    validate={(value: string) => {
                        if (!/^[a-z0-9-]+$/.test(value)) {
                            return 'Name must contain only lowercase letters, numbers, and hyphens';
                        }
                        return undefined;
                    }}
                />
                <NumberInput
                    source="expirationDays"
                    defaultValue={30}
                    min={1}
                    max={365}
                    helperText="Number of days until token expires"
                />
                <NumberInput
                    source="maxUsage"
                    min={1}
                    helperText="Leave blank for unlimited usage"
                />
                <NumberInput
                    source="rateLimitPerHour"
                    defaultValue={10}
                    min={1}
                    max={1000}
                    helperText="Maximum requests per hour"
                />
            </SimpleForm>
        </Create>
    );
};
